"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { BlogPost } from "@/lib/blog-supabase";
import { trackBlogPostRead, trackBlogScrollDepth } from "@/lib/analytics";

interface Props {
  post: BlogPost;
  related: BlogPost[];
}

function renderContent(content: string): string {
  let html = content
    .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold text-navy mt-10 mb-4">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-navy mt-8 mb-3">$1</h3>')
    .replace(/^#### (.+)$/gm, '<h4 class="text-lg font-semibold text-navy mt-6 mb-2">$1</h4>')
    .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-gold bg-gold/5 pl-5 py-3 my-6 text-navy/70 italic rounded-r-lg">$1</blockquote>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-navy">$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-gold hover:text-gold/80 font-medium underline underline-offset-2 transition-colors">$1</a>')
    .replace(/^- (.+)$/gm, '<li class="flex items-start gap-2 mb-1.5"><span class="mt-2 w-1.5 h-1.5 flex-shrink-0 rounded-full bg-gold"></span><span>$1</span></li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="flex items-start gap-3 mb-2"><span class="flex-shrink-0 w-6 h-6 rounded-full bg-navy text-white text-xs flex items-center justify-center font-bold">$1</span><span>$2</span></li>');

  const lines = html.split('\n');
  const result: string[] = [];
  let inList = false;

  for (const line of lines) {
    const isLi = line.trim().startsWith('<li');
    const isBlock = line.trim().startsWith('<h') || line.trim().startsWith('<blockquote');

    if (isLi && !inList) {
      result.push('<ul class="space-y-1 my-4 pl-2">');
      inList = true;
    } else if (!isLi && inList) {
      result.push('</ul>');
      inList = false;
    }

    if (!isLi && !isBlock && line.trim() !== '') {
      result.push(`<p class="text-navy/70 leading-relaxed mb-4">${line}</p>`);
    } else {
      result.push(line);
    }
  }

  if (inList) result.push('</ul>');
  return result.join('\n');
}

export default function BlogPostContent({ post, related }: Props) {
  const articleRef = useRef<HTMLElement>(null);
  const shareUrl = typeof window !== "undefined" ? window.location.href : `https://www.premierpropertymgt.net/blog/${post.slug}`;
  const shareTitle = encodeURIComponent(post.title);
  const shareUrlEncoded = encodeURIComponent(shareUrl);

  const formattedDate = new Date(post.publishDate).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  useEffect(() => {
    trackBlogPostRead(post.slug, post.title, post.category, post.readTime);
  }, [post.slug, post.title, post.category, post.readTime]);

  useEffect(() => {
    const milestones = [25, 50, 75, 100];
    const reached = new Set<number>();

    const handleScroll = () => {
      const article = articleRef.current;
      if (!article) return;
      const { top, height } = article.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrolled = Math.max(0, windowHeight - top);
      const percent = Math.min(100, Math.round((scrolled / height) * 100));
      milestones.forEach((m) => {
        if (percent >= m && !reached.has(m)) {
          reached.add(m);
          trackBlogScrollDepth(post.slug, m);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post.slug]);

  return (
    <>
      <article ref={articleRef} className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pt-10 pb-16">
        <nav className="flex items-center gap-2 text-sm text-navy/40 mb-8">
          <Link href="/homepage" className="hover:text-navy transition-colors">Home</Link>
          <span>/</span>
          <Link href="/blog" className="hover:text-navy transition-colors">Blog</Link>
          <span>/</span>
          <span className="text-navy/60 truncate max-w-xs">{post.title}</span>
        </nav>

        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs font-semibold text-gold uppercase tracking-wider bg-gold/10 px-3 py-1.5 rounded-full">
            {post.category}
          </span>
          <span className="text-sm text-navy/40 flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {post.readTime} min read
          </span>
          <span className="text-sm text-navy/40">{formattedDate}</span>
          {post.isAutoGenerated && (
            <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
              AI Generated
            </span>
          )}
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy leading-tight mb-6">
          {post.title}
        </h1>

        <p className="text-lg text-navy/60 leading-relaxed mb-8 border-l-4 border-gold pl-5">
          {post.excerpt}
        </p>

        <div className="flex items-center justify-between flex-wrap gap-4 pb-8 border-b border-sand-dark mb-8">
          <div className="flex items-center gap-3">
            <img
              src={post.authorAvatar}
              alt={`${post.authorName} - ${post.authorRole}`}
              className="w-12 h-12 rounded-full object-cover border-2 border-gold/30"
            />
            <div>
              <p className="font-semibold text-navy text-sm">{post.authorName}</p>
              <p className="text-xs text-navy/50">{post.authorRole}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-navy/40 mr-1">Share:</span>
            <a
              href={`https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrlEncoded}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-sand hover:bg-blue-50 flex items-center justify-center transition-colors"
              aria-label="Share on Twitter"
            >
              <svg className="w-4 h-4 text-navy/40" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-sand hover:bg-blue-50 flex items-center justify-center transition-colors"
              aria-label="Share on Facebook"
            >
              <svg className="w-4 h-4 text-navy/40" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrlEncoded}&title=${shareTitle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-sand hover:bg-blue-50 flex items-center justify-center transition-colors"
              aria-label="Share on LinkedIn"
            >
              <svg className="w-4 h-4 text-navy/40" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>

        {post.featuredImage && (
          <div className="rounded-2xl overflow-hidden mb-10 aspect-video">
            <img
              src={post.featuredImage}
              alt={post.featuredImageAlt}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div
          className="text-navy/70 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
        />

        <div className="mt-12 pt-8 border-t border-sand-dark">
          <p className="text-sm font-semibold text-navy mb-3">Tags:</p>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span key={tag} className="text-sm text-navy/60 bg-sand px-3 py-1 rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-navy rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">
            Ready to Maximize Your PCB Rental Income?
          </h3>
          <p className="text-white/60 mb-6">
            Get a free revenue estimate from Panama City Beach&apos;s Premier Property MGT team.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/contact" className="btn-gold px-8 py-3 rounded-full font-semibold text-sm">
              Get Free Quote
            </Link>
            <Link
              href="/services"
              className="px-8 py-3 rounded-full font-semibold text-sm border border-white/20 text-white hover:bg-white/10 transition-colors"
            >
              View Services
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <h2 className="text-2xl font-bold text-navy mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((relPost) => (
                <article key={relPost.slug} className="group">
                  <Link href={`/blog/${relPost.slug}`} className="block overflow-hidden rounded-xl aspect-video mb-4">
                    <img
                      src={relPost.featuredImage}
                      alt={relPost.featuredImageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </Link>
                  <span className="text-xs font-semibold text-gold uppercase tracking-wider">{relPost.category}</span>
                  <Link href={`/blog/${relPost.slug}`}>
                    <h3 className="text-base font-bold text-navy mt-1 mb-2 group-hover:text-gold transition-colors line-clamp-2">
                      {relPost.title}
                    </h3>
                  </Link>
                  <p className="text-sm text-navy/50 line-clamp-2">{relPost.excerpt}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
